import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Store } from 'lucide-react';
import Swal from 'sweetalert2';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('cajero');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('¡Bienvenido!');
      } else {
        await register({ email, password, full_name: fullName, role });
        toast.success('Cuenta creada exitosamente. Por favor inicia sesión.');
        setIsLogin(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al procesar solicitud';
      if (typeof errorMsg === 'string' && errorMsg.includes('Membresía Suspendida')) {
         Swal.fire({
            icon: 'error',
            title: 'Acceso Denegado',
            text: errorMsg,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#d33'
         });
      } else {
         toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-indigo-50 via-white to-sky-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      
      {/* Glassmorphic card overlay */}
      <Card 
        className="w-full max-w-md bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border border-white/60 dark:border-slate-800/80 shadow-2xl relative z-20 rounded-3xl" 
        data-testid="login-card"
        style={{
          boxShadow: '0 20px 45px -15px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
        }}
      >
        <CardHeader className="space-y-2 text-center pt-8 pb-4">
          <div className="flex justify-center mb-2">
            <div className="p-3 bg-indigo-600 rounded-2xl shadow-md transform hover:scale-105 transition-transform">
              <Store className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-slate-950 to-indigo-950 dark:from-white dark:to-slate-200 bg-clip-text text-transparent">
            GKService
          </CardTitle>
          <CardDescription className="text-slate-500 dark:text-slate-400 text-sm">
            {isLogin ? 'Ingresa tus credenciales para acceder' : 'Crea una cuenta en el sistema'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-slate-700 dark:text-slate-300 text-xs font-semibold">Nombre Completo</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  data-testid="fullname-input"
                  className="bg-white/60 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800/80 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-xl h-11"
                  placeholder="Ej: Juan Pérez"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 text-xs font-semibold">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                required
                data-testid="email-input"
                className="bg-white/60 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800/80 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-xl h-11"
                placeholder="ejemplo@empresa.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 text-xs font-semibold">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="bg-white/60 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800/80 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-xl h-11"
                placeholder="••••••••"
              />
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-700 dark:text-slate-300 text-xs font-semibold">Rol del Usuario</Label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-200 dark:border-slate-800/80 bg-white/60 dark:bg-slate-900/60 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  data-testid="role-select"
                >
                  <option value="cajero" className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white">Cajero</option>
                  <option value="bodeguero" className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white">Bodeguero</option>
                  <option value="admin" className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white">Administrador</option>
                </select>
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full rounded-xl h-12 font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all pt-0.5"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Iniciando sesión...' : isLogin ? 'Ingresar al Portal' : 'Registrarse'}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 font-semibold transition-colors hover:underline"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate aquí' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}